import {fetchStudentContestResultRequest} from 'APIs/letrusAPI/studentContestResult';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import SidebarLayout from 'layouts/SidebarLayout';
import {LoadingBox} from 'letrus-ui';
import {useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import ContestAwardCard from './components/ContestAwardCard';
import ContestCertificateCard from './components/ContestCertificateCard';
import styles from './ContestResult.module.scss';

function ContestResult(): JSX.Element {
  const navigate = useNavigate();
  const contestResultRequest = fetchStudentContestResultRequest();
  const contestResult = contestResultRequest.data;
  const result = contestResult?.result;

  const {navigateToLearningPath} = useLearningPathNavigation(
    result?.learning_path_user_instance_id?.toString(),
  );
  const isScoreUnavailable = result?.final_score == null;

  const formattedScore = useMemo(() => {
    if (result?.final_score == null) {
      return 'Não disponível';
    }

    const rawScore = result.final_score;
    const numericScore = Number(rawScore);

    if (Number.isNaN(numericScore)) {
      return rawScore.replace('.', ',');
    }

    return numericScore.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    });
  }, [result?.final_score]);

  const availableScoreDescription =
    'Essa é sua nota final na Olimpíada. Ela é composta por 60% da nota da redação + 40% da nota do questionário. Quer conferir cada resultado separadamente?';

  function handleGoBack() {
    navigate(-1);
  }

  function handleOpenLearningPath() {
    if (!result?.learning_path_user_instance_id) return;

    navigateToLearningPath.lastReviewOrFirstStep();
  }

  if (contestResultRequest.isLoading) {
    return (
      <SidebarLayout>
        <main className={styles.container}>
          <section className={styles.header} aria-hidden="true">
            <LoadingBox width="2rem" height="2rem" borderRadius={999} />

            <div className={styles.titleContainer}>
              <LoadingBox width="12rem" height="1rem" borderRadius={5} />
              <LoadingBox width="18rem" height="1.75rem" borderRadius={5} />
              <LoadingBox width="10rem" height="1rem" borderRadius={5} />
            </div>
          </section>

          <section className={styles.resultCard} aria-hidden="true">
            <div className={styles.resultCardHeader}>
              <LoadingBox width="8rem" height="1.5rem" borderRadius={5} />
            </div>

            <div className={styles.scoreWrapper}>
              <LoadingBox width="10rem" height="4rem" borderRadius={8} />
              <LoadingBox width="6rem" height="1rem" borderRadius={5} />
            </div>

            <div className={styles.divider} aria-hidden="true" />

            <LoadingBox width="100%" height="1rem" borderRadius={5} />
            <LoadingBox width="100%" height="1rem" borderRadius={5} />
            <LoadingBox width="75%" height="1rem" borderRadius={5} />
          </section>

          <section className={styles.resultCard} aria-hidden="true">
            <LoadingBox width="100%" height="16rem" borderRadius={16} />
          </section>
        </main>
      </SidebarLayout>
    );
  }

  if (contestResultRequest.error || !result) {
    return (
      <SidebarLayout>
        <main className={styles.container}>
          <section className={styles.feedbackCard}>
            <span className={styles.feedbackKicker}>
              Olimpíada Linguagem em Ação
            </span>
            <h1 className={styles.feedbackTitle}>
              Não foi possível carregar seu resultado
            </h1>
            <p className={styles.feedbackDescription}>
              Tente novamente em alguns instantes. Se o problema continuar,
              atualize a página ou volte mais tarde.
            </p>
          </section>
        </main>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <main className={styles.container}>
        <header className={styles.header}>
          <button
            type="button"
            onClick={handleGoBack}
            className={styles.goBackButton}
            aria-label="Voltar para Olimpíada Linguagem em Ação"
          >
            <FontAwesomeIcon icon="chevron-left" color="#FF4410" size="2x" />
          </button>

          <div className={styles.titleContainer}>
            <span className={styles.kicker}>Olimpíada Linguagem em Ação</span>
            <h1 className={styles.title}>Seu resultado já está disponível!</h1>
            <p className={styles.subtitle}>
              Confira sua nota e descubra se tem algum reconhecimento te
              esperando.
            </p>
          </div>
        </header>

        <section className={styles.resultCard} aria-labelledby="final-score">
          <div className={styles.resultCardHeader}>
            <h2 id="final-score">Nota final</h2>
          </div>

          <div className={styles.scoreWrapper}>
            <strong
              className={`${styles.score} ${
                isScoreUnavailable ? styles.unavailableScore : ''
              }`}
            >
              {formattedScore}
            </strong>
            {!isScoreUnavailable && (
              <span className={styles.scoreUnit}>de 100 pontos</span>
            )}
          </div>

          <div className={styles.divider} aria-hidden="true" />

          {isScoreUnavailable ? (
            <p className={styles.resultDescription}>
              Para calcular sua nota final na Olimpíada, precisamos dos
              resultados da redação e do questionário. Como não temos o registro
              completo das duas etapas, não foi possível gerar sua nota final.
              <br />
              <br />
              Mas sua participação está registrada e você pode emitir seu
              certificado abaixo.
            </p>
          ) : (
            <p className={styles.resultDescription}>
              {availableScoreDescription}{' '}
              <button
                type="button"
                onClick={handleOpenLearningPath}
                className={styles.inlineAction}
                disabled={!result?.learning_path_user_instance_id}
              >
                Clique aqui.
              </button>
            </p>
          )}
        </section>

        <ContestAwardCard result={result} />

        <ContestCertificateCard result={result} />
      </main>
    </SidebarLayout>
  );
}

export default ContestResult;
